import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';

const NotificationSystem = ({
  isNotificationsOpen,
  setIsNotificationsOpen,
  notifications,
  isLoading,
  error,
  unreadCount,
  markAllAsRead,
  markAsRead,
  formatPhoneNumber
}) => {
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  // Keep ESC key functionality
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isNotificationsOpen) {
        setIsNotificationsOpen(false);
      }
    };

    if (isNotificationsOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isNotificationsOpen, setIsNotificationsOpen]);

  // REMOVE the outside click handler to prevent automatic closing

  // Handle notification click without closing the panel
  const handleNotificationClick = (notification, e) => {
    // First mark as read if needed
    if (!notification.read) {
      setTimeout(() => {
        markAsRead(notification.id);
      }, 0);
    }
    
    // Close the notifications panel
    setIsNotificationsOpen(false);
  };

const createLinkState = (notification) => {
  const notifDateObj = new Date(notification.dateBonCommande);
  const currentDate = new Date();
  
  // Create start date as first day of the order's month
  const startDate = new Date(notifDateObj.getFullYear(), notifDateObj.getMonth(), 1);
  
  // Determine the end date based on current date vs order month
  let endDate;
  
  // Check if we're still in the same month and year as the order
  const isSameMonth = currentDate.getFullYear() === notifDateObj.getFullYear() && 
                     currentDate.getMonth() === notifDateObj.getMonth();
  
  if (isSameMonth) {
    // If we're still in the order's month, use current date
    endDate = new Date(currentDate);
  } else {
    // If we're past the order's month, use the last day of the order's month
    endDate = new Date(notifDateObj.getFullYear(), notifDateObj.getMonth() + 1, 0); // Day 0 gives last day of previous month
  }
  
  // Format dates to YYYY-MM-DD in local timezone to avoid UTC conversion issues
  const formatDate = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const startDateStr = formatDate(startDate);
  const endDateStr = formatDate(endDate);

  const state = {
    fromNotification: true,
    filterPhoneNumber: notification.numero,
    filterStartDate: startDateStr,
    filterEndDate: endDateStr
  };
  
  console.log(`Order date: ${notification.dateBonCommande}, Start: ${startDateStr}, End: ${endDateStr}`);
  
  return state;
};

  // Add this format date helper function after your imports
  const formatDateString = (dateString) => {
    try {
      const date = new Date(dateString);
      // Format as "23 mai 2025" (or whatever format you prefer in French)
      return date.toLocaleDateString('fr-FR', { 
        day: 'numeric', 
        month: 'long', 
        year: 'numeric' 
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return "Date inconnue";
    }
  };

  return (
    <div className="relative z-50" ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
        className="relative p-1.5 text-gray-500 hover:text-gray-800 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
        aria-label="Notifications"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white transform translate-x-1/2 -translate-y-1/2 bg-red-600 rounded-full min-w-[1rem] h-4">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Fixed Dropdown Panel */}
      {isNotificationsOpen && (
        <div 
          className="fixed top-0 right-0 bottom-0 md:absolute md:top-auto md:bottom-auto md:right-0 md:mt-2 md:origin-top-right bg-white shadow-lg rounded-md w-full md:min-w-[400px] md:max-w-[450px] border border-gray-200 z-50 h-full md:h-auto md:max-h-[80vh] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()} // Prevent clicks from bubbling up
        >
          {/* Dropdown Header */}
          <div className="px-4 py-3 bg-white border-b border-gray-200 flex items-center justify-between sticky top-0 z-10 rounded-t-md">
            <div className="flex items-center">
              <h3 className="text-base font-medium text-gray-900">Notifications</h3>
              {unreadCount > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                  {unreadCount}
                </span>
              )}
            </div>
            <div className="flex items-center space-x-2">
              {unreadCount > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    markAllAsRead();
                  }}
                  className="text-sm text-[#174540] hover:text-[#0d2926] font-medium"
                >
                  Tout marquer
                </button>
              )}
              {/* ONLY THIS BUTTON CLOSES THE MENU */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsNotificationsOpen(false);
                }}
                className="text-gray-400 hover:text-gray-500 focus:outline-none p-2" // Made bigger for easier tapping
                aria-label="Fermer"
              >
                <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Notification Content Area */}
          <div 
            className="overflow-y-auto flex-1 max-h-[calc(100%-120px)]" // Set explicit max height
            style={{
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(28, 86, 81, 0.5) transparent'
            }}
          >
            <style jsx>{`
              div::-webkit-scrollbar {
                width: 4px;
              }
              
              div::-webkit-scrollbar-track {
                background: transparent;
                margin: 4px 0;
              }
              
              div::-webkit-scrollbar-thumb {
                background-color: rgba(28, 86, 81, 0.5);
                border-radius: 20px;
                transition: background-color 0.3s;
              }
              
              div::-webkit-scrollbar-thumb:hover {
                background-color: rgba(28, 86, 81, 0.8);
              }
            `}</style>
            

            {isLoading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin h-8 w-8 border-4 border-[#174540] border-t-transparent rounded-full"></div>
                <p className="mt-2 text-sm text-gray-500">Chargement...</p>
              </div>
            ) : error ? (
              <div className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-2">
                  <svg className="h-6 w-6 text-red-500" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <p className="text-sm text-gray-800 font-medium">Erreur de chargement</p>
                <p className="text-xs text-gray-500 mt-1 mb-2">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="text-xs text-[#174540] underline hover:text-[#0d2926]"
                >
                  Réessayer
                </button>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-8 text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-gray-100 mb-2">
                  <svg className="h-6 w-6 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z" />
                  </svg>
                </div>
                <p className="text-sm text-gray-800 font-medium">Aucune notification</p>
                <p className="text-xs text-gray-500 mt-1">Vous verrez ici les nouvelles commandes</p>
              </div>
            ) : (
              <div>
                {/* Group notifications by date */}
                {Object.entries(
                  notifications.reduce((groups, notification) => {
                    // Create a formatted date from the dateBonCommande property
                    const date = formatDateString(notification.dateBonCommande);
                    if (!groups[date]) groups[date] = [];
                    groups[date].push(notification);
                    return groups;
                  }, {})
                ).map(([date, groupedNotifications]) => (
                  <div key={date}>
                    <div className="px-4 py-2 bg-gray-50 text-xs font-medium text-gray-500 sticky top-0 z-20 border-b border-gray-200">
                      {date}
                    </div>
                    {groupedNotifications.map((notification) => (
                      // Using Link component directly for reliable navigation
                      <Link
                        key={notification.id}
                        to="/admin/orders"
                        state={createLinkState(notification)}
                        className={`block px-4 py-3 border-b border-gray-100 hover:bg-gray-50 active:bg-gray-100 transition-colors duration-150 ${notification.read ? '' : 'bg-blue-50/60'}`}
                        onClick={() => handleNotificationClick(notification)}
                      >
                        <div className="flex items-start">
                          <div className="flex-shrink-0 bg-gradient-to-br from-[#174540] to-[#0d2926] h-10 w-10 rounded-full flex items-center justify-center text-white shadow-sm">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                            </svg>
                          </div>
                          <div className="ml-3 flex-1">
                            <p className="text-sm font-medium text-gray-900">
                              Le client <span className="text-[#174540] font-semibold">{formatPhoneNumber(notification.numero)}</span> a passé <span className="font-semibold">{notification.count}</span> commandes
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {date.toLowerCase()} • Cliquez pour voir les commandes
                            </p>
                          </div>
                          {!notification.read && (
                            <span className="ml-2 mt-1.5 inline-block h-2 w-2 flex-shrink-0 rounded-full bg-[#174540]"></span>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {!isLoading && !error && notifications.length > 0 && (
            <div className="p-3 bg-gray-50 border-t border-gray-200 sticky bottom-0">
              <Link
                to="/admin/orders"
                onClick={() => setIsNotificationsOpen(false)} // Add this to close panel
                className="block w-full px-4 py-2 text-center text-sm font-medium text-white bg-[#174540] rounded hover:bg-[#123735] transition-colors"
              >
                Voir toutes les commandes
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationSystem;